import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Network, 
  Cable, 
  Wrench, 
  MapPin, 
  Users, 
  Shield, 
  FileCheck,
  Zap,
  CheckCircle2
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1742774101928-f7e92a471c1b?auto=format&fit=crop&w=1920&q=80";
const FIBER_IMG = "https://images.unsplash.com/photo-1543273984-bb8287d2fc4e?auto=format&fit=crop&w=800&q=80";
const COAX_IMG = "https://images.unsplash.com/photo-1595856619767-ab739fa7daae?auto=format&fit=crop&w=800&q=80";
const SPLICING_IMG = "https://images.pexels.com/photos/11392632/pexels-photo-11392632.jpeg?auto=compress&w=800";

const services = [
  {
    title: "Fiber Network Construction",
    description: "Full OSP fiber builds, backbone and distribution network deployment, new builds, upgrades, and expansions.",
    icon: Network,
    image: FIBER_IMG,
    link: "/services#fiber"
  },
  {
    title: "Coax / HFC Network Construction",
    description: "Broadband network builds and rebuilds including strand, coaxial infrastructure, and HFC support services.",
    icon: Cable,
    image: COAX_IMG,
    link: "/services#coax"
  },
  {
    title: "Fiber Splicing & Testing",
    description: "Fusion splicing, termination, OTDR testing, troubleshooting, and network restoration support.",
    icon: Wrench,
    image: SPLICING_IMG,
    link: "/services#splicing"
  }
];

const whyChooseUs = [
  { text: "Strong presence in Ohio", icon: MapPin },
  { text: "Ability to deploy crews nationwide", icon: Users },
  { text: "Experience working alongside major carriers", icon: Network },
  { text: "Safety-driven operations", icon: Shield },
  { text: "Clean documentation & testing reports", icon: FileCheck },
  { text: "Fast mobilization capability", icon: Zap }
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="hero-section"
      >
        <div className="absolute inset-0 hero-overlay"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl">
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-[#00B4D8]/20 border border-[#00B4D8]/40 text-[#00B4D8] text-xs font-bold uppercase tracking-wider">
                Telecommunications Infrastructure
              </span>
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-black tracking-tight leading-none text-white mb-6 animate-fade-in">
              Carrier-Grade Fiber & HFC Network Construction
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-4 animate-fade-in stagger-1">
              OSP Fiber Construction • Coax/HFC Network Builds • Fiber Splicing
            </p>
            <p className="text-lg text-slate-400 mb-10 animate-fade-in stagger-2">
              Based in Ohio – Deploying Nationwide
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-3">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-[#00B4D8] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.3)]"
                data-testid="hero-cta-quote"
              >
                Request a Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-transparent text-white border-2 border-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-white hover:text-slate-900 transition-all duration-300"
                data-testid="hero-cta-contact"
              >
                Contact Our Team
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Snapshot */}
      <section className="py-20 md:py-32 bg-white" data-testid="about-snapshot-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
                About C&F Communications
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
                Field-Proven Telecommunications Contractor
              </h2>
              <div className="section-line mb-8"></div>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                C&F Communications is a field-proven telecommunications contractor specializing in 
                Outside Plant (OSP) fiber optic construction, HFC/coax network deployment, and 
                high-precision fiber splicing services.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                With strong operational presence in Ohio and the ability to mobilize 
                nationwide, we support carriers, prime contractors, and broadband operators with 
                scalable crews, disciplined execution, and safety-first standards.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center text-[#00B4D8] font-semibold hover:underline"
                data-testid="about-learn-more"
              >
                Learn more about us
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 p-8 border-l-4 border-[#00B4D8]">
                <div className="text-4xl font-black text-[#00B4D8] mb-2">OH</div>
                <div className="text-sm font-medium text-slate-600 uppercase tracking-wider">Ohio Headquarters</div>
              </div>
              <div className="bg-slate-50 p-8 border-l-4 border-[#00B4D8]">
                <div className="text-4xl font-black text-[#00B4D8] mb-2">2022</div>
                <div className="text-sm font-medium text-slate-600 uppercase tracking-wider">Founded Feb 2022</div>
              </div>
              <div className="bg-slate-50 p-8 border-l-4 border-[#F59E0B] col-span-2">
                <div className="text-4xl font-black text-[#F59E0B] mb-2">USA</div>
                <div className="text-sm font-medium text-slate-600 uppercase tracking-wider">Nationwide Deployment</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-20 md:py-32 bg-slate-50 grid-pattern" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
              What We Do
            </span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mt-4 mb-6">
              Core Services
            </h2>
            <div className="section-line mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Link
                key={service.title}
                to={service.link}
                className="group relative overflow-hidden bg-white border border-slate-200 hover:border-[#00B4D8] transition-all duration-300 service-card"
                data-testid={`service-card-${index}`}
              >
                <div className="aspect-video relative overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <service.icon className="h-8 w-8 text-[#00B4D8]" />
                  </div>
                </div>
                <div className="p-8">
                  <h3 className="font-heading text-xl font-bold text-slate-900 mb-3 group-hover:text-[#00B4D8] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center text-[#00B4D8] text-sm font-semibold group-hover:underline">
                    Learn More
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-[#0F172A] text-white" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-[#00B4D8] text-sm font-bold uppercase tracking-wider">
                Why Partner With Us
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mt-4 mb-6">
                Why Choose C&F Communications
              </h2>
              <div className="section-line mb-8"></div>
              <p className="text-lg text-slate-400 leading-relaxed mb-8">
                We deliver telecommunications infrastructure with reliability, precision, and 
                compliance with carrier standards. Our team is built on experience, safety, 
                and the capability to scale.
              </p>
              <Link
                to="/capabilities"
                className="inline-flex items-center justify-center bg-[#00B4D8] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.2)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.2)]"
                data-testid="why-choose-cta"
              >
                View Our Capabilities
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {whyChooseUs.map((item, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-4 bg-slate-800/50 p-6 border border-slate-700"
                  data-testid={`why-choose-item-${index}`}
                >
                  <div className="flex-shrink-0">
                    <item.icon className="h-6 w-6 text-[#00B4D8]" />
                  </div>
                  <span className="text-sm font-medium text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#00B4D8]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Next Project?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Contact us today to discuss your telecommunications infrastructure needs.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-white text-[#0F172A] px-8 py-4 text-sm font-bold uppercase tracking-wider transition-all duration-300 shadow-[4px_4px_0px_0px_rgba(15,23,42,0.3)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(15,23,42,0.3)]"
            data-testid="bottom-cta-button"
          >
            Request Project Discussion
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
